import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FaGlobe } from 'react-icons/fa';
import intlShape from '@/shared/utils/intlShape';
import EditableDatePicker from '@/shared/components/editable/EditableDatePicker';
import moment from 'moment';
import EditableAutoComplete from '@/shared/components/editable/EditableAutoComplete';

import FileIcon from '@/shared/components/FileIcon';
import FormattedListValue, { listItemMsg } from '@/shared/components/FormattedListValue';
import RelationName from '@/shared/components/RelationName';
import DocumentService from '@/shared/services/DocumentService';
import Checkbox from '@/shared/components/input/Checkbox';
import { countVisibleColumns } from '@/shared/utils/hiddenColumnsHelper';
import DataCacheService from '@/shared/services/DataCacheService';
import DocumentSigningStatusIcon from '@/shared/components/DocumentSigningStatusIcon';
import messages from './documentMessages';
import DocumentActionsButton from './DocumentActionsButton';
import RecognitionStatus from './RecognitionStatus';
import DocumentDetailRow, { documentTypesWithDetails } from './DocumentDetailRow';
import FormattedDateShort from '../../shared/components/FormattedDateShort';
import EditableDocumentTypeSelect from './EditableDocumentTypeSelect';
import AuthService from '@/shared/services/AuthService';
import { OneDriveIcon, SharepointIcon } from '@/shared/components/Icons';

export default class DocumentRow extends React.Component {
  state = {
    isCollapsed: true,
  };

  toggleCollapsed = () => {
    const { isCollapsed } = this.state;
    const { document, loadRecognitionMeta } = this.props;
    if (isCollapsed && typeof document.recognitionMeta === 'undefined')
      loadRecognitionMeta(document);

    this.setState({
      isCollapsed: !isCollapsed,
    });
  };

  render() {
    const {
      document,
      action,
      onOpenDocumentDetails,
      intl,
      handleSelection,
      onDeleteDocument,
      onCreateTodo,
      onOpenSignatureRequestModal,
      onSendViaEmail,
      onOpenRevokeSignatureRequestModal,
      onShareItem,
      onDeleteRelation,
      onForward,
      columnStatus,
      onUpdateDocumentDate,
      onUpdateDocumentType,
      onSendToSharepoint,
      openInApp,
      duplicate,
      onReplaceDocument,
      onCheckinDocument,
      onCheckoutDocument,
      onDiscardCheckout,
      actionsInProgress,
      onExtractDocumentData,
    } = this.props;
    const { isCollapsed } = this.state;

    // A row is collapsable when it has been recognized, not processed and metadata about the document was found
    const isCollapsable =
      document.recognitionStatus === 2 &&
      (typeof document.recognitionMeta === 'undefined' || !!document.recognitionMeta) &&
      (documentTypesWithDetails.indexOf(document.documentType) >= 0 ||
        document.hasRelationImportModel);
    // Count of all <td> elements + 1 for column for toggle collapse
    const totalColumns = 4 + countVisibleColumns(columnStatus);

    return (
      <tbody className="hover">
        <tr
          key={document.id}
          draggable
          className="draggable document"
          onDragStart={e => {
            e.dataTransfer.effectAllowed = 'all';
            e.dataTransfer.setData('text', JSON.stringify(document));
            try {
              e.dataTransfer.setData('cxdocument', JSON.stringify(document));
            } catch (ex) {
              // Ignore
            }
          }}
        >
          <td className="hidden-xs">
            <Checkbox onClick={() => handleSelection(document)} value={document.selected} />
          </td>

          <td className="overflow-ellipsis" title="">
            <FileIcon document={document} />
            <span
              className="text-clickable"
              onClick={() => onOpenDocumentDetails(document)}
              data-toggle="tooltip"
              data-placement="bottom"
              title={document.name}
            >
              {document.name}
            </span>
            <span className="pull-right">
              {isCollapsable && (
                <a onClick={this.toggleCollapsed}>
                  <i
                    className={classNames('fa pointer', {
                      'fa-caret-up': !isCollapsed,
                      'fa-caret-down': isCollapsed,
                    })}
                  />
                </a>
              )}
            </span>
          </td>

          {columnStatus.documentType ? (
            document.canRemove ? (
              <td className="document-type overflow-ellipsis">
                <EditableDocumentTypeSelect
                  value={document.documentType}
                  required
                  onChange={x => onUpdateDocumentType(x)}
                />
              </td>
            ) : (
              <td
                className="fw-normal overflow-ellipsis"
                title={intl.formatMessage(listItemMsg('documenttype', document.documentType))}
              >
                {intl.formatMessage(listItemMsg('documenttype', document.documentType))}
              </td>
            )
          ) : null}
          {columnStatus.relation && (
            <td>
              {document.relations &&
                document.relations.slice(0, 3).map(relation => (
                  // <span className="margin10R" key={relation.id}>
                  <RelationName
                    key={relation.id}
                    relation={relation}
                    onDeleteRelation={
                      document.canRemove ? relationId => onDeleteRelation(relationId) : null
                    }
                  />
                  // </span>
                ))}
              {document.relations && document.relations.length > 3 && (
                <span
                  title={intl.formatMessage(messages.NrOfLinkedRelations, {
                    nr: document.relations.length,
                  })}
                >
                  &hellip;
                </span>
              )}
            </td>
          )}
          {columnStatus.recognitionStatus && (
            <td className="hidden-xs">
              <RecognitionStatus status={document.recognitionStatus} />
            </td>
          )}

          {columnStatus.documentDate && (
            <td className="hidden-xs">
              <EditableDatePicker
                showClearDate={false}
                value={
                  typeof document.documentDate === 'string'
                    ? moment.utc(document.documentDate)
                    : document.documentDate
                }
                onChange={documentDate => onUpdateDocumentDate(documentDate)}
              />
            </td>
          )}
          {columnStatus.creationDate && (
            <td className="hidden-xs fw-normal">
              <FormattedDateShort value={document.creationDate} />
            </td>
          )}
          {columnStatus.createdBy && (
            <td className="hidden-xs fw-normal">
              {document.creationUserId
                ? DataCacheService.getUserName(document.creationUserId)
                : null}
            </td>
          )}
          <td className="extra-icon-column hidden-xs">
            {document.isPublicOnPortal === true && (
              <div className="btn-group margin10R">
                <FaGlobe
                  title={intl.formatMessage(messages.DocumentIsPublicOnPortal)}
                  size="1.5em"
                />
              </div>
            )}
            {document.canBeSigned &&
              (document.shouldBeSigned || document.signingStatus > 1) &&
              document.signingStatus && <DocumentSigningStatusIcon document={document} />}

            {document.storageType === 1 && (
              <div className="btn-group margin10R">
                <a
                  onClick={() => DocumentService.edit(document.id, intl.locale.split('-')[0])}
                  target="_blank"
                  rel="noreferrer nofollow"
                  className="btn btn-xs btn-inverse"
                  title={intl.formatMessage(messages.EditDocumentInSharePoint)}
                  style={{ borderRadius: '3px' }}
                >
                  <SharepointIcon title={intl.formatMessage(messages.EditDocumentInSharePoint)} />
                </a>
              </div>
            )}
            {document.storageType != 1 && document.checkedOutByUserId != null && (
              <div className="btn-group margin10R">
                <span
                  onClick={() => {
                    if (document.checkedOutByUserId == AuthService.getUserId())
                      DocumentService.getCheckedOutLink(document.id).then(link => {
                        if (!link) return;
                        window.open(link);
                      });
                  }}
                  target="_blank"
                  rel="noreferrer nofollow"
                  className="btn btn-xs btn-inverse"
                  title={intl.formatMessage(messages.DocumentCheckedOut, {
                    name: DataCacheService.getUserName(document.checkedOutByUserId),
                  })}
                  style={{ borderRadius: '3px' }}
                >
                  <OneDriveIcon
                    title={intl.formatMessage(messages.DocumentCheckedOut, {
                      name: DataCacheService.getUserName(document.checkedOutByUserId),
                    })}
                  />
                </span>
              </div>
            )}
          </td>
          <td className="actions-column hidden-xs">
            <div className="flex-row-nowrap" style={{ justifyContent: 'flex-end' }}>
              {AuthService.hasFeature('AI') &&
                DocumentService.supportsDataExtraction(document.documentType) &&
                document.relations &&
                document.relations.length > 0 &&
                AuthService.hasPermission({ functionality: 'Documents', privilege: 'Create' }) && (
                  <a
                    className="btn btn-xs btn-icon"
                    onClick={() => onExtractDocumentData(document)}
                    disabled={document.extracting}
                    title={
                      document.extracting
                        ? intl.formatMessage(messages.ExtractionInProgress)
                        : intl.formatMessage(messages.ExtractDocumentData)
                    }
                  >
                    {document.extracting ? (
                      <i className="fa fa-spinner fa-spin fa-fw" />
                    ) : (
                      <i className="fa fa-magic fa-fw" />
                    )}
                  </a>
                )}
              <a
                className="btn btn-xs btn-icon"
                onClick={() => DocumentService.download(document.id)}
              >
                <i className="fa fa-cloud-download fa-fw" />
              </a>
              {action && (
                <DocumentActionsButton
                  document={document}
                  suggestedAction={action.suggestedAction}
                  otherActions={action.otherActions}
                  onDeleteDocument={onDeleteDocument}
                  onCreateTodo={onCreateTodo}
                  onOpenSignatureRequestModal={onOpenSignatureRequestModal}
                  onOpenRevokeSignatureRequestModal={onOpenRevokeSignatureRequestModal}
                  onSendViaEmail={onSendViaEmail}
                  onShareItem={onShareItem}
                  onForward={onForward}
                  onSendToSharepoint={onSendToSharepoint}
                  openInApp={openInApp}
                  duplicate={duplicate}
                  onReplaceDocument={onReplaceDocument}
                  onCheckinDocument={onCheckinDocument}
                  onCheckoutDocument={onCheckoutDocument}
                  onDiscardCheckout={onDiscardCheckout}
                  actionsInProgress={actionsInProgress}
                />
              )}
            </div>
          </td>
        </tr>
        {!isCollapsed && isCollapsable && (
          <tr>
            <td />
            <td colSpan={totalColumns - 1}>
              <DocumentDetailRow document={document} />{' '}
            </td>
          </tr>
        )}
      </tbody>
    );
  }
}

DocumentRow.propTypes = {
  document: PropTypes.shape({}).isRequired,
  intl: intlShape.isRequired,
  action: PropTypes.shape({}).isRequired,
  onOpenDocumentDetails: PropTypes.func.isRequired,
  handleSelection: PropTypes.func.isRequired,
  loadRecognitionMeta: PropTypes.func.isRequired,
  columnStatus: PropTypes.shape({}).isRequired,
  onUpdateDocumentDate: PropTypes.func.isRequired,
  openInApp: PropTypes.func.isRequired,
  duplicate: PropTypes.func.isRequired,
  onReplaceDocument: PropTypes.func.isRequired,
  onExtractDocumentData: PropTypes.func.isRequired,
};
